<script setup lang="ts">
import AlertBanner from "@/abilities/data-process/features/import-configurations/components/alert-banner.vue";
import Dialog from "@/shared/components/dialog/dialog.vue";
import {
    Language,
    useCreatePdfCatalogRequestAndSendEmailMutation,
} from "@/shared/services/graphql/generated/admin-graph-types";
import { useLanguageStore } from "@/shared/store/language-store";
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";

const { availableLocales, uiLocale } = storeToRefs(useLanguageStore());

type LanguageOption = { label: string; value: string };

const props = defineProps<{
    productId: string;
    assetId?: string;
    notifyAfterAction: (wasCanceled: boolean) => void;
}>();

const exportResult = ref<"success" | "error" | null>(null);
const exportErrorMessage = ref("");
const exportRequestId = ref("");

const {
    mutate: createPdfCatalog,
    loading: exportLoading,
    onDone: onExportDone,
    onError: onExportError,
} = useCreatePdfCatalogRequestAndSendEmailMutation();

onExportDone((result) => {
    exportResult.value = "success";
    exportRequestId.value = result.data?.createPdfCatalogRequestAndSendEmail?.requestId ?? "";
});

onExportError((error) => {
    exportResult.value = "error";
    exportErrorMessage.value = error.message;
});

const languageMapping: Record<string, string> = {
    DE: "Deutsch",
    EN: "English",
    FR: "Français",
    IT: "Italiano",
    ES: "Español",
    ZH: "中文",
    JA: "日本語",
};

const languageOptions = computed<LanguageOption[]>(() => {
    const available = availableLocales.value.map((l) => l.toUpperCase());
    return available.map((lang) => ({
        label: languageMapping[lang] || lang,
        value: lang,
    }));
});

const selectedLanguage = ref<LanguageOption | null>(null);
const showSecondDropdown = ref(false);
const secondLanguage = ref<LanguageOption | null>(null);

const firstLanguageOptions = computed<LanguageOption[]>(() => {
    return languageOptions.value.filter((opt) => opt.value !== secondLanguage.value?.value);
});

const secondLanguageOptions = computed<LanguageOption[]>(() => {
    return languageOptions.value.filter((opt) => opt.value !== selectedLanguage.value?.value);
});

const hasAdditionalLanguages = computed(() => {
    return secondLanguageOptions.value.length > 0;
});

const isExportDisabled = computed(() => {
    return (
        !selectedLanguage.value ||
        exportResult.value !== null ||
        exportLoading.value ||
        (showSecondDropdown.value && !secondLanguage.value)
    );
});

function removeSecondLanguage() {
    showSecondDropdown.value = false;
    secondLanguage.value = null;
}

function closeDialog() {
    props.notifyAfterAction(exportResult.value !== "success");
}

function exportPdf() {
    const languages: Language[] = [selectedLanguage.value!.value.toLowerCase() as Language];
    if (showSecondDropdown.value && secondLanguage.value) {
        languages.push(secondLanguage.value.value.toLowerCase() as Language);
    }

    createPdfCatalog({
        pdfRequestData: {
            productId: props.productId,
            assetId: props.assetId ?? null,
            languages,
        },
    });
}

// Initialize with current UI language
const currentLang = uiLocale.value?.toUpperCase() || "";
const current = languageOptions.value.find((opt) => opt.value === currentLang);
selectedLanguage.value = current || languageOptions.value[0] || null;
</script>

<template>
    <Dialog visible @on-close="closeDialog">
        <template #header>
            <div class="export-dialog-header">
                <q-icon name="fa-regular fa-file-pdf" color="primary" class="q-mr-sm" />
                <div class="flex-grow text-weight-bold dialog-title">
                    {{ $t("product-and-asset.Export product catalog as PDF") }}
                </div>
                <div>
                    <q-btn
                        round
                        size="md"
                        icon="fa fa-solid fa-xmark"
                        @click="closeDialog"
                        data-qs="dialog-close-btn"
                        dense
                    />
                </div>
            </div>
        </template>
        <template #content>
            <div v-if="exportResult === 'success'" class="q-mb-md">
                <div class="q-mb-sm">
                    {{ $t("product-and-asset.The catalog is now being exported") }}
                </div>
                <div class="q-mb-sm">
                    {{
                        $t(
                            "product-and-asset.This may take some time You will receive an email notification once the file is available for download"
                        )
                    }}
                </div>
                <q-badge outline color="grey-14" :label="'ID: ' + exportRequestId" />
            </div>
            <div v-else-if="exportResult === 'error'" class="q-mb-md">
                <AlertBanner color="negative" light>
                    <div class="row items-start q-gutter-md">
                        <q-icon name="fa-regular fa-triangle-exclamation" size="sm" color="negative" />
                        <div class="col">
                            <div class="text-weight-semibold q-mb-sm">
                                {{ $t("product-and-asset.The export could not be started") }}
                            </div>
                            <div class="text-body2">{{ exportErrorMessage }}</div>
                        </div>
                    </div>
                </AlertBanner>
            </div>
            <div class="export-configuration-body" v-else>
                <div class="dialog-body-text">
                    {{ $t("product-and-asset.In which languages should the parts catalog be exported") }}
                </div>
                <div class="export-language-selection">
                    <q-select
                        v-model="selectedLanguage"
                        :options="firstLanguageOptions"
                        dense
                        outlined
                        class="language-dropdown"
                    />
                    <q-btn
                        v-if="!showSecondDropdown && hasAdditionalLanguages"
                        icon="fa-regular fa-plus"
                        color="secondary"
                        dense
                        no-caps
                        class="square-button"
                        @click="showSecondDropdown = true"
                    >
                        <q-tooltip>{{ $t("product-and-asset.Add another language") }}</q-tooltip>
                    </q-btn>
                    <q-select
                        v-if="showSecondDropdown"
                        v-model="secondLanguage"
                        :options="secondLanguageOptions"
                        :placeholder="$t('product-and-asset.Additional language')"
                        dense
                        outlined
                        hide-bottom-space
                        class="language-dropdown"
                    />
                    <q-btn
                        v-if="showSecondDropdown"
                        icon="fa-regular fa-xmark"
                        color="secondary"
                        outlined
                        dense
                        class="square-button"
                        @click="removeSecondLanguage"
                    >
                        <q-tooltip>{{ $t("product-and-asset.Remove additional language") }}</q-tooltip>
                    </q-btn>
                </div>
                <div class="row justify-end q-gutter-sm">
                    <q-btn :label="$t('product-and-asset.Cancel')" color="secondary" no-caps @click="closeDialog" />
                    <q-btn
                        :disabled="isExportDisabled"
                        :loading="exportLoading"
                        color="primary"
                        no-caps
                        @click="exportPdf"
                    >
                        <q-icon name="fa-solid fa-arrow-right" size="xs" class="q-mr-sm" />
                        {{ $t("product-and-asset.Export catalog") }}
                    </q-btn>
                </div>
            </div>
        </template>
    </Dialog>
</template>

<style lang="scss" scoped>
.export-configuration-body {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .export-language-selection {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;

        .square-button {
            width: 40px;
            height: 40px;
        }

        .language-dropdown {
            width: 170px;
            min-width: 170px;
        }
    }
}

.export-dialog-header {
    display: flex;
    flex-direction: row;
    .dialog-title {
        font-size: var(--font-size-Big, 16px);
    }

    .dialog-body-text {
        font-size: var(--font-size-Regular, 14px);
    }
}
</style>
